import { useNode } from '@craftjs/core';
import { Box, InputAdornment, Stack, useTheme } from '@mui/material';
import { useCallback } from 'react';

import { useSyncTextEditorState } from '@/components/blockEditor/hooks/useSyncTextEditorState';
import { ColorBox } from '@/components/display/ColorBox/ColorBox';
import { InputLabel } from '@/components/form/baseInputs/InputLabel';
import { SelectInput } from '@/components/form/baseInputs/SelectInput/SelectInput';
import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { SimpleTextToolbar } from '@/components/TextEditor/plugins/ToolbarPlugin/SimpleTextToolbar';
import { TextEditor } from '@/components/TextEditor/TextEditor';

import { PresentationListItemProps } from './PresentationListItem';
import {
  getDefaultIconColorToken,
  getIconColorFromToken,
  ICON_COLOR_TOKEN_OPTIONS,
  ICON_TYPE_OPTIONS,
  IconColorToken,
  renderIconFromType,
} from './PresentationListItem.utils';

export function PresentationListItemSettings() {
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props as PresentationListItemProps,
  }));
  const theme = useTheme();

  const updateDescription = useCallback(
    (newContent: string) => {
      setProp((props: PresentationListItemProps) => {
        props.description = newContent;
      });
    },
    [setProp]
  );

  const {
    editorKey: descriptionEditorKey,
    handleUpdate: handleDescriptionUpdate,
  } = useSyncTextEditorState(
    String(props.description || ''),
    updateDescription
  );

  // Don't pass iconColor here so each icon type shows its default color in the dropdown
  const iconOptions = ICON_TYPE_OPTIONS.map((option) => ({
    ...option,
    startAdornment: (
      <InputAdornment position="start">
        {renderIconFromType({
          iconType: option.value,
          theme,
        })}
      </InputAdornment>
    ),
  }));

  const iconColorOptions = ICON_COLOR_TOKEN_OPTIONS.map((option) => ({
    ...option,
    startAdornment: (
      <InputAdornment position="start">
        <ColorBox
          color={getIconColorFromToken(theme, option.value)}
          width={16}
        />
      </InputAdornment>
    ),
  }));

  return (
    <Stack spacing={2}>
      <SelectInput
        label="Icon style"
        name="iconType"
        value={props.iconType}
        onChange={(e) => {
          const newIconType = e.target
            .value as PresentationListItemProps['iconType'];
          setProp((props: PresentationListItemProps) => {
            props.iconType = newIconType;
            // Update icon color to the default for this icon type
            props.iconColor = getDefaultIconColorToken(newIconType);
          });
        }}
        options={iconOptions}
      />
      <SelectInput
        label="Icon color"
        name="iconColor"
        value={props.iconColor}
        onChange={(e) => {
          setProp((props: PresentationListItemProps) => {
            props.iconColor = e.target.value as IconColorToken;
          });
        }}
        options={iconColorOptions}
        showEmptyValue={false}
      />
      {props.iconType === 'number' && (
        <TextInput
          label="List item number"
          name="number"
          value={props.number}
          onChange={(e) => {
            setProp((props: PresentationListItemProps) => {
              props.number = parseInt(e.target.value) || undefined;
            });
          }}
        />
      )}
      <TextInput
        label="Title"
        name="title"
        value={props.title}
        onChange={(e) => {
          setProp((props: PresentationListItemProps) => {
            props.title = e.target.value;
          });
        }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <InputLabel>Description</InputLabel>
        <Box
          sx={{
            borderRadius: '4px',
          }}
        >
          <TextEditor
            key={descriptionEditorKey}
            initialValue={String(props.description || '')}
            initialValueKind="markdown"
            onUpdate={handleDescriptionUpdate}
            ToolbarPlugin={SimpleTextToolbar}
          />
        </Box>
      </Box>
    </Stack>
  );
}
